import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { CheckCircle, XCircle, Spinner } from "@phosphor-icons/react";
import { api } from "@/lib/api";

const POLL_INTERVALS = [0, 2000, 2000, 2000, 3000, 3000, 5000];

export default function PaymentResult({ outcome = "success" }) {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState(outcome === "cancel" ? "cancelled" : "checking");
  const navigate = useNavigate();

  const poll = useCallback(
    async (attempt) => {
      if (attempt >= POLL_INTERVALS.length) {
        setState("timeout");
        return;
      }
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setState("failed");
          return;
        }
      } catch {
        setState("failed");
        return;
      }
      setTimeout(() => poll(attempt + 1), POLL_INTERVALS[attempt + 1] || 3000);
    },
    [sessionId]
  );

  useEffect(() => {
    if (outcome === "cancel") return;
    if (!sessionId) {
      setState("failed");
      return;
    }
    poll(0);
  }, [outcome, sessionId, poll]);

  const views = {
    checking: { icon: Spinner, tone: "text-[#002FA7]", title: "Confirming your payment…", body: "Hold on while Stripe confirms the transaction. Do not close this window." },
    paid: { icon: CheckCircle, tone: "text-[#0B7A5B]", title: "Payment received", body: "The purchase order is now marked paid. A record is kept under Purchase Orders." },
    failed: { icon: XCircle, tone: "text-[#FF3B30]", title: "Payment did not go through", body: "Nothing was charged. You can try paying the order again." },
    timeout: { icon: Spinner, tone: "text-[#8A6A00]", title: "Still processing", body: "This is taking longer than usual. Check the order in a minute — the status updates automatically." },
    cancelled: { icon: XCircle, tone: "text-slate-500", title: "Payment cancelled", body: "You left the payment page, so nothing was charged." },
  };
  const v = views[state] || views.checking;
  const Icon = v.icon;

  return (
    <div className="grid-lines flex min-h-screen items-center justify-center bg-[#F8FAFC] p-4">
      <div data-testid="payment-result" data-state={state} className="w-full max-w-md border border-border bg-white p-8 text-center">
        <Icon size={44} className={`mx-auto ${v.tone} ${state === "checking" ? "animate-spin" : ""}`} />
        <h1 data-testid="payment-result-title" className="font-display mt-4 text-2xl font-bold tracking-tight text-[#0F172A]">
          {v.title}
        </h1>
        <p className="mt-2 text-sm text-slate-500">{v.body}</p>
        <div className="mt-6 flex flex-col gap-2">
          <Link
            data-testid="back-to-orders"
            to="/orders"
            className="border border-[#002FA7] bg-[#002FA7] px-4 py-3 text-xs font-semibold uppercase tracking-widest text-white transition-colors duration-150 hover:bg-[#00227A]"
          >
            Back to purchase orders
          </Link>
          <button
            data-testid="back-to-dashboard"
            onClick={() => navigate("/dashboard")}
            className="border border-border px-4 py-3 text-xs font-semibold uppercase tracking-widest text-slate-600 transition-colors duration-150 hover:bg-slate-50"
          >
            Dashboard
          </button>
        </div>
      </div>
    </div>
  );
}
